import { NavLink } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Logo } from '@growhero/brand';
import { cn } from '@/lib/cn';
import { formatNumber } from '@/lib/format';
import { useTenant } from '@/tenant/TenantProvider';
import { useInsights } from '@/features/insights/api';
import { ProMark } from '@/ui';
import type { NavArea, NavGroup } from '../nav';
import { CreditMeters } from './CreditMeters';
import { TenantSwitcher } from './TenantSwitcher';
import { UserBar } from './UserBar';

/**
 * Icon colour per product area. The sidebar sits in `surface-field`, which swaps the area tokens
 * for their light variants, so these pass on the blue (≥5.3:1). Neutral items use on-field-subtle.
 */
export const areaIcon: Record<NavArea | 'none', string> = {
  ai: 'text-area-ai',
  reviews: 'text-area-reviews',
  messages: 'text-area-messages',
  none: 'text-on-field-subtle',
};

/** Open AI proposals, shown as a saffron count next to «Προτάσεις» (Pro only). */
function useNavCounts(): Record<string, number> {
  const { hasFeature, current } = useTenant();
  const enabled = !!current && hasFeature('insights');
  // Same key as the insights page (limit 20), so the badge shares its cache.
  const q = useInsights('open', { enabled });
  const open = enabled ? (q.data?.counts.open ?? 0) : 0;
  return open > 0 ? { insights: open } : {};
}

/** Saffron count pill (A3: saffron marks the one number that needs attention). */
export function NavCount({ value, label }: { value: number; label: string }) {
  return (
    <span
      className="num inline-flex h-5 min-w-5.5 items-center justify-center rounded-full bg-field-accent px-1.5 text-2xs font-extrabold text-on-accent"
      aria-label={label}
    >
      {value > 99 ? '99+' : formatNumber(value)}
    </span>
  );
}

export function NavList({
  groups,
  onNavigate,
  showPro = true,
}: {
  groups: NavGroup[];
  onNavigate?: () => void;
  showPro?: boolean;
}) {
  const { t } = useTranslation('nav');
  const { hasFeature, current } = useTenant();
  const counts = useNavCounts();
  return (
    <nav aria-label={t('mainNav')} className="grid gap-4">
      {groups.map((g) => (
        <div key={g.id} className="grid gap-0.5">
          {g.labelKey && (
            <div className="eyebrow mb-1 px-2.5 text-[0.65625rem] tracking-[0.14em] text-on-field-subtle">
              {t(g.labelKey)}
            </div>
          )}
          {g.items.map((it) => {
            const locked = showPro && !!it.feature && !!current && !hasFeature(it.feature);
            const count = !locked ? counts[it.id] : undefined;
            const Icon = it.icon;
            return (
              <NavLink
                key={it.id}
                to={it.to}
                end={it.end ?? it.to === '/'}
                onClick={onNavigate}
                data-area={it.area}
                className={({ isActive }) =>
                  cn(
                    'group relative flex h-9 items-center gap-3 rounded-sm px-2.5 text-[0.875rem] transition-[background-color,color] duration-150',
                    'focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-field-focus',
                    // Active: the white pill of the A3 sidebar (explicit white, not a swapped token).
                    isActive
                      ? 'bg-white font-bold text-field shadow-[0_1px_2px_rgb(4_18_34/0.25)]'
                      : 'font-medium text-on-field-muted hover:bg-field-raised hover:text-on-field',
                  )
                }
              >
                {({ isActive }) => (
                  <>
                    <Icon
                      weight={isActive ? 'duotone' : 'regular'}
                      className={cn(
                        'size-[18px] shrink-0 transition-colors',
                        // On the white pill: the brand blue (constant field tone, 7:1 on white).
                        isActive ? 'text-field-tonal-strong' : areaIcon[it.area ?? 'none'],
                      )}
                    />
                    <span className="flex-1 truncate">{t(it.labelKey)}</span>
                    {locked && <ProMark variant={isActive ? 'solid' : 'outline'} />}
                    {count ? <NavCount value={count} label={t('countOpen', { count })} /> : null}
                  </>
                )}
              </NavLink>
            );
          })}
        </div>
      ))}
    </nav>
  );
}

/**
 * Full sidebar contents (used by the desktop rail and the mobile drawer). Render it inside
 * `surface-field`: the A3 sidebar is the deep sea-blue brand field.
 */
export function SidebarContent({
  groups,
  onNavigate,
  variant = 'portal',
}: {
  groups: NavGroup[];
  onNavigate?: () => void;
  variant?: 'portal' | 'admin';
}) {
  const { t } = useTranslation('nav');
  return (
    <div className="flex h-full flex-col overflow-y-auto overscroll-contain">
      <div className="flex items-center justify-between px-5 pb-4 pt-5">
        <NavLink
          to={variant === 'admin' ? '/platform-admin' : '/'}
          onClick={onNavigate}
          className="rounded-sm focus-visible:outline-2 focus-visible:outline-field-focus"
          aria-label="GrowHero"
        >
          <Logo height={26} tone="field" title="GrowHero" />
        </NavLink>
        {variant === 'admin' && (
          <span className="eyebrow rounded-[4px] bg-field-accent px-1.5 py-0.5 text-[0.625rem] text-on-accent">
            {t('admin.badge')}
          </span>
        )}
      </div>
      {variant === 'portal' && (
        <div className="px-3 pb-3">
          <TenantSwitcher />
        </div>
      )}
      <div className="flex-1 px-3 pb-4 pt-1">
        <NavList groups={groups} onNavigate={onNavigate} showPro={variant === 'portal'} />
      </div>
      {variant === 'portal' && (
        <div className="px-3 pb-3">
          <CreditMeters onNavigate={onNavigate} />
        </div>
      )}
      <div className="border-t border-field-line px-3 py-3">
        <UserBar variant={variant} onNavigate={onNavigate} />
      </div>
    </div>
  );
}
